import { graphql } from "@repo/graphql-types/gql";
import { Graph, Right } from "@repo/icons";
import { formatWithZonedDate, ServicePackageEnum } from "@repo/lib";
import { Link, useRouteContext } from "@tanstack/react-router";
import { endOfMonth, startOfMonth } from "date-fns";
import { useGraphQL } from "@/hooks/use-graphql.ts";
import {
  SectionHeaderButton,
  SectionHeaderContent,
  SectionHeaderDescription,
  SectionHeaderIcon,
  SectionHeaderInfo,
  SectionHeaderRoot,
  SectionHeaderTitle,
} from "@/components/section-header";
import { HomeSectionLivanceAssistCardGroup } from "@/components/home/home-section-livance-assist-card-group.tsx";
import { AsyncDataWrapper } from "@/components/async-data-wrapper.tsx";
import { HomeSectionAssistEmptyState } from "@/components/home/home-section-assist-empty-state";
import { ErrorCard } from "@/components/home/error-card";
import { LivanceAssistCardGroupSkeleton } from "@/components/home/Skeleton/livance-assist-card-group-skeleton.tsx";

export const HomeSectionLivanceAssistQuery = graphql(`
  query HomeSectionLivanceAssistQuery(
    $fromStr: String!
    $toStr: String!
    $fromDateTime: datetime2!
    $toDateTime: datetime2!
  ) {
    ...HomeSectionLivanceAssistCardGroupQueryFragment
  }
`);

const STALE_TIME = 3_600_000; // 1 hora

export const HomeSectionLivanceAssist = (): JSX.Element => {
  const { user } = useRouteContext({ strict: false });

  const isAssistProUser = user.pacotesServicos.find(
    (servicePkg) => servicePkg.codPacoteServicos === ServicePackageEnum.Pro.valueOf(),
  );

  const now = new Date();
  const startDate = formatWithZonedDate(startOfMonth(now), "yyyy-MM-dd HH:mm:ss-03:00");
  const endDate = formatWithZonedDate(endOfMonth(now), "yyyy-MM-dd 23:59:59-03:00");

  const queryResult = useGraphQL(
    HomeSectionLivanceAssistQuery,
    {
      fromStr: startDate,
      toStr: endDate,
      fromDateTime: startDate,
      toDateTime: endDate,
    },
    {
      enabled: Boolean(isAssistProUser),
      staleTime: STALE_TIME,
    },
  );

  const { data, refetch } = queryResult;

  const periodDetails = `${formatWithZonedDate(startDate, "dd MMMM yyyy")} - ${formatWithZonedDate(endDate, "dd MMMM yyyy")}`;
  const refetchAssistQuery = (): void => {
    void refetch();
  };

  const renderContent = (): JSX.Element => {
    if (!isAssistProUser) {
      return <HomeSectionAssistEmptyState />;
    }

    return (
      <AsyncDataWrapper
        {...queryResult}
        errorFallback={<ErrorCard onClick={refetchAssistQuery} />}
        fallback={<LivanceAssistCardGroupSkeleton />}
      >
        {data ? (
          <HomeSectionLivanceAssistCardGroup data={data} periodDetails={periodDetails} />
        ) : null}
      </AsyncDataWrapper>
    );
  };

  return (
    <div className="gap-4 flex flex-col">
      <SectionHeaderRoot>
        <SectionHeaderContent>
          <SectionHeaderIcon className="bg-white">
            <Graph size={16} className="fill-primary-400" />
          </SectionHeaderIcon>
          <SectionHeaderInfo>
            <SectionHeaderTitle>Livance Assist</SectionHeaderTitle>
            <SectionHeaderDescription>Gestão do seu consultório</SectionHeaderDescription>
          </SectionHeaderInfo>
        </SectionHeaderContent>
        {isAssistProUser ? (
          <Link to="/settings/assist" search={{ from: "/home" }}>
            <SectionHeaderButton>
              Ver todos <Right size={16} />
            </SectionHeaderButton>
          </Link>
        ) : (
          <div />
        )}
      </SectionHeaderRoot>

      {renderContent()}
    </div>
  );
};
